import type { Plan } from '@t/productOffering.types';
import { usePlanFlowMutation, variablesPlan } from './usePlanFlowMutation';
import { useBundleRelationshipFlow } from './useBundleRelationshipFlow';

type UseSelectPlanFlowReturn = {
  handlePlanSelected: (plan: Plan) => void;
  handleSwitchPlan: (oldOfferingId: string, newPlan: Plan) => void;
  /** Opens the bundle relationship dialog in edit mode, pre-populated with the plan's existing child selections. */
  openBundleForEdit: (plan: Plan) => void;
  pendingPlanId: string | null;
  /** Non-null when the selected EV plan has bundle relationships and needs a selection dialog. */
  bundleSelectionPlan: Plan | null;
  /** Offering IDs of the plan's currently-selected children — used to pre-populate the dialog in edit mode. */
  bundleEditInitialIds: string[];
  /** Called when the user confirms selected offerings from the bundle dialog. */
  handleBundleRelationshipConfirmed: (mainPlan: Plan, selectedOfferings: Plan[]) => Promise<void>;
  /** Called when the user dismisses the bundle dialog without picking. */
  handleBundleRelationshipDismissed: () => void;
  isBundleConfirming: boolean;
};

export const useSelectPlanFlow = (sessionId: string): UseSelectPlanFlowReturn => {
  const planFlow = usePlanFlowMutation(sessionId);

  const mutationBundlePlan =
    !planFlow.isPending && planFlow.data?.hasBundleRelationship ? planFlow.data.plan : null;

  const bundleFlow = useBundleRelationshipFlow({
    sessionId,
    mutationBundlePlan,
    onAfterNewConfirm: () => planFlow.reset(),
  });

  const handlePlanSelected = (plan: Plan): void => {
    planFlow.mutate({ kind: 'select', plan });
  };

  const handleSwitchPlan = (oldOfferingId: string, newPlan: Plan): void => {
    planFlow.mutate({ kind: 'switch', oldOfferingId, newPlan });
  };

  const pendingPlanId =
    planFlow.isPending && planFlow.variables ? variablesPlan(planFlow.variables).planId : null;

  const handleBundleRelationshipDismissed = (): void => {
    bundleFlow.handleBundleRelationshipDismissed();
    planFlow.reset();
  };

  return {
    handlePlanSelected,
    handleSwitchPlan,
    openBundleForEdit: bundleFlow.openBundleForEdit,
    pendingPlanId,
    bundleSelectionPlan: bundleFlow.bundleSelectionPlan,
    bundleEditInitialIds: bundleFlow.bundleEditInitialIds,
    handleBundleRelationshipConfirmed: bundleFlow.handleBundleRelationshipConfirmed,
    handleBundleRelationshipDismissed,
    isBundleConfirming: bundleFlow.isBundleConfirming,
  };
};
